import clsx from 'clsx'
import { useMemo } from 'react'
import { styled } from 'styles'
import { BREACHRX_CIRM_COMPANY_ID, routes } from 'configs/constants'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { DownArrowIcon, IncidentIcon, SettingsIcon, EditIcon, KeyIcon, InsuranceIcon, EmailIcon } from 'components/icons'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/collapsible'
import { Box, Flex } from 'components/elements'
import { Assessment } from '@mui/icons-material'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'
import { useGetIncidentApplicableCount } from 'features/incidents/api/getIncidentApplicableCount'
import { Button } from 'components/button'
import { Zendesk_Email } from 'features/incidents/components/enums'
// import { useGetBitSightReport } from 'features/profile/api/bitSightReport'
// import { useEffect, useState } from 'react'
// import { CircularProgress, Tooltip } from '@mui/material'
// import { errorMessage, getUpperCase } from 'utils/function'
import { useGetPolicies } from 'features/insurance/api/getPolicies'
// import { ToolTipcss } from 'features/incidents/components/style'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import { TERMINOLOGY } from 'utils/enum'

const SECURE_SCORE_SERVICE_ID = 6
type SidebarItem = {
  name: string
  icon: React.ReactNode
  path?: string
  disabled?: boolean
  children?: SidebarItem[]
  notificationCount?: number
}
export interface ErrorResponse {
  errorMessage: string
}
const sidebarConfig: SidebarItem[] = [
  {
    name: TERMINOLOGY.warranty.singular,
    icon: <InsuranceIcon />,
    path: routes.insurancePolicy,
    disabled: false,
  },
  {
    name: 'Incidents',
    icon: <IncidentIcon />,
    path: routes.incidents,
    disabled: false,
    notificationCount: 76,
  },
  {
    name: 'Monitoring Services',
    icon: <Assessment />,
    path: routes.assessments,
    disabled: false,
  },
  {
    name: 'Secure Score',
    icon: <DatasetOutlinedIcon />,
    path: routes.secureScore,
    disabled: false,
  },
  {
    name: 'Inventory',
    icon: <InventoryOutlinedIcon />,
    path: routes.inventory,
    disabled: false,
  },
  {
    name: 'Settings',
    icon: <SettingsIcon />,
    children: [
      {
        name: 'Edit Profile',
        icon: <EditIcon />,
        path: routes.editProfile,
        disabled: false,
      },
      {
        name: 'Change Password',
        icon: <KeyIcon />,
        path: routes.changePass,
        disabled: false,
      },
    ],
  },
]

function SidebarMenu() {
  const { pathname } = useLocation()
  //set trigger the get bitsight report on click
  // const [fetchReport, setFetchReport] = useState(false)
  // const [showReportTooltip, setShowReportTooltip] = useState(false) // State to control tooltip visibility

  const { data } = useGetIncidentApplicableCount()
  //get bitsight report
  // const { data: bitsightReport, isFetching, error: apiError } = useGetBitSightReport(fetchReport)
  //get user current Policy
  const {
    data: currentPolicy,
    // isLoading: PolicyLoading
  } = useGetPolicies()
  //primary service id
  const planPrimaryService = currentPolicy?.data?.primaryServiceId
  const userServices = currentPolicy?.data?.services || []
  const hasActivePolicy =
    // Exclude if the only active service is secure service
    !(userServices?.length === 1 && userServices[0]?.serviceId === 6) && userServices?.some((service: { isActive: boolean }) => service.isActive)

  const isClaimEnabled = currentPolicy?.data?.isClaimEnabled
  // BreachRx CIRM runs on the Secure Score integration but is sold as its own
  // service, so the holder has no Secure Score of their own to look at. Keyed on
  // the carrier so every warranty BreachRx writes is covered, not just the first.
  const isBreachRxCirmPlan = currentPolicy?.data?.companyId === BREACHRX_CIRM_COMPANY_ID

  const showIncidents = useMemo(() => {
    // BreachRx holders claim against the warranty by hand rather than off a
    // detected incident, and the Incidents page is where "Initiate Claim
    // Manually" lives — so the tab stays whatever the plan's claim flag says.
    if (isBreachRxCirmPlan) {
      return true
    }
    if (planPrimaryService === SECURE_SCORE_SERVICE_ID && isClaimEnabled === false) {
      return false
    }
    return true
  }, [planPrimaryService, isClaimEnabled, isBreachRxCirmPlan])

  // const hasActivePolicy = currentPolicy?.data?.services?.some((service: { isActive: boolean }) => service.isActive)
  //report generatedCount Check
  // const reportGeneratedCount = currentPolicy?.data?.report_generated
  //is report already generated
  // const isReportGenerated = reportGeneratedCount > 0
  const potentialClaim = data?.data?.applicableClaimCount
  //api error
  // const bitSightError = errorMessage(apiError) || 'Unexpected Error Occured'

  //dowbload bitsight pdf report
  // useEffect(() => {
  //   if (bitsightReport?.data?.pdf_url) {
  //     const link = document.createElement('a')
  //     link.href = bitsightReport?.data?.pdf_url // Set the URL for the download
  //     link.download = 'BitSightReport.pdf' // Optional: Set a default file name
  //     link.target = '_blank' // Open in a new tab if needed
  //     link.click() // Trigger the download
  //     setFetchReport(false)
  //     // Show tooltip when report is downloaded
  //     setShowReportTooltip(true)
  //     setTimeout(() => {
  //       setShowReportTooltip(false) // Hide tooltip after 5 seconds
  //     }, 5000)
  //   }
  // }, [bitsightReport])
  //api error handling
  // useEffect(() => {
  //   if (apiError) {
  //     setFetchReport(false)
  //   }
  // }, [apiError])
  // const handleFetchReport = () => {
  //   setFetchReport(true) // Trigger the query
  // }
  const getMenuItem = (entry: any, isNested = false) => (
    <Box as="li" key={entry.path}>
      <MenuLink to={entry.path} className={clsx({ active: entry.path === pathname, nested: isNested })}>
        {entry.icon}
        <span>{entry.name}</span>
        {!!entry.notificationCount && (
          <>
            {potentialClaim > 0 && (
              <NotificationNumber>
                <Box as="p" css={{ m: 0 }}>
                  {potentialClaim}
                </Box>
              </NotificationNumber>
            )}
          </>
        )}
      </MenuLink>
    </Box>
  )
  const filteredSidebarConfig: SidebarItem[] = sidebarConfig.filter(item => {
    if (item.name === 'Incidents' && !showIncidents) return false
    if (item.name === 'Inventory' && !hasActivePolicy) return false
    if (item.name === 'Secure Score' && isBreachRxCirmPlan) return false
    return true
  })
  return (
    <MenuWrapper>
      <MenuList>
        {filteredSidebarConfig.map((entry, idx) => {
          if (!entry.children) {
            return getMenuItem(entry)
          }
          return (
            <>
              <Collapsible key={idx} defaultOpen={true} asChild>
                <Box as="li">
                  <CollapsibleTrigger asChild>
                    <MenuLink as="button" className="is--setting">
                      <SettingsIcon />
                      <span>Settings</span>
                      <DownArrowIcon fontSize={14} style={{ marginLeft: 'auto' }} className="svg-icon" />
                    </MenuLink>
                  </CollapsibleTrigger>

                  <CollapsibleContent asChild>
                    <MenuList>{entry.children.map(subEntry => getMenuItem(subEntry, true))}</MenuList>
                  </CollapsibleContent>
                </Box>
              </Collapsible>
            </>
          )
        })}
      </MenuList>
      <Flex justify={'center'} direction={'column'} css={{ 'gap': '1rem', 'margin': '$10 0 $3 0', '@sm': { margin: '0.8rem 0' } }}>
        {/* {!!apiError && (
          <Box as="span" css={{ color: '$red', textAlign: 'center' }}>
            {getUpperCase(bitSightError)}
          </Box>
        )} */}

        {/* <Tooltip
          PopperProps={{ sx: ToolTipcss }}
          title={
            <Box as="span" css={{ color: '$black' }}>
              {showReportTooltip
                ? 'Report downloaded successfully!'
                : 'You have availed the detailed company report feature already. In case you wanted to download the report, please contact your administrator.'}
            </Box>
          }
          placement="bottom"
          enterTouchDelay={0}
          disableHoverListener={!isReportGenerated && !showReportTooltip} // Disable hover if report is not generated
        >
          <Button
            css={{ ...SidebarMenuButtonCss, cursor: isReportGenerated ? 'not-allowed' : 'pointer', opacity: isReportGenerated ? 0.5 : 1 }}
            onClick={() => {
              //reportGeneratedcount 0 means report not generated
              if (!isReportGenerated) {
                handleFetchReport()
              }
            }}
            disabled={isFetching || PolicyLoading}
          >
            {isFetching ? <CircularProgress size={18} sx={{ color: 'var(--colors-white) !important' }} /> : 'Generate Bitsight Report'}
          </Button>
        </Tooltip> */}
        <Box css={{ alignSelf: 'center' }} as="a" href={`mailto:${Zendesk_Email.EMAIL}`}>
          <Button css={SidebarMenuButtonCss}>
            <Flex align="center" css={{ gap: '0.5rem' }}>
              <Box css={{ fontWeight: '$semibold' }}>Support</Box>
              <EmailIcon />
            </Flex>
          </Button>
        </Box>
      </Flex>
    </MenuWrapper>
  )
}

const MenuWrapper = styled('div', {
  'display': 'flex',
  'flexDirection': 'column',
  'flex': 1,
  'pt': 30,
  'pb': 20,
  'justifyContent': 'space-between',
  'overflowY': 'scroll',
  '&::-webkit-scrollbar': {
    width: '4px',
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '2px',
  },

  '&::-webkit-scrollbar-thumb': {
    background: '$white',
    borderRadius: '2px',
  },
})

const NotificationNumber = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.5rem',
  height: '2.5rem',
  p: '.5rem',
  background: '$red',
  borderRadius: '50%',
})

export const MenuList = styled('ul', {
  'listStyle': 'none',
  'padding': 0,
  'margin': 0,

  'li ul li': {
    borderTop: '1px solid $gray3',
  },
})

export const MenuLink = styled(RouterLink, {
  'display': 'flex',
  'cursor': 'pointer',
  'border': 'none',
  'background': 'none',
  'width': '100%',
  'alignItems': 'center',
  'justifyContent': 'flex-start',
  'padding': '16px 24px 16px 34px',
  'height': 60,
  'gap': 14,

  '&[data-state]': {
    '.svg-icon': {
      transition: 'transform .25s linear',
    },
  },

  '&.is--setting': {
    '&[data-state="open"]': {
      '.svg-icon': {
        transform: 'rotate(180deg)',
      },
    },
  },

  '&.nested': {
    paddingLeft: 54,
  },
  '&.active': {
    background: '$primary',
    borderRadius: '0 6px 6px 0',
  },
  'svg': {
    fontSize: 28,
  },
  'span': {
    fontSize: 16,
    fontWeight: 500,
  },
})

const SidebarMenuButtonCss = {
  borderRadius: '$base',
  border: 'none',
  width: '190px',
  fontSize: '14px',
  alignSelf: 'center',
  padding: '0.58rem 1.25rem',
}
export default SidebarMenu
